import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { format, isValid } from "date-fns";

// MUI Components
import { Button, TextField } from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Icons
import { ReactComponent as UPIIcon } from "assets/upi.svg";
import { ReactComponent as CashIcon } from "assets/cash.svg";

// Local Components
import BankForm from "../BankForm";
import { StyledRefundDetails, StyledRefundDetailsDrawer } from "./styled";
import CheckmarkControl from "components/v2/Common/CheckmarkControl";
import BottomSheetDrawer from "components/v2/Common/BottomSheetDrawer";
import FloatingCloseButton from "components/v2/Common/FloatingCloseButton";

// Reducers
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";

// API
import { submitDetailsApi, verifyBankDetailsApi } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";
import { stripVersionPrefix } from "utils/routes";

const BANK_VERIFICATION_FALLBACK = "Something is wrong. Please try again.";
const DETAILS_SUBMISSION_FALLBACK = "Error! Please try again in some time.";

const FAILED_STATUSES = ["fail", "expire", "reversal", "cancel", "deactivate"];
const SUCCESS_STATUSES = ["processed", "complete", "redeemed", "paid"];

const formatDatePart = (value, dateFormat) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return isValid(date) ? format(date, dateFormat) : null;
};

const formatTimestamp = (value) => {
  const date = formatDatePart(value, "dd MMM yyyy");
  const time = formatDatePart(value, "hh:mm aa");
  return { date, time };
};

const formatTitleCase = (value, fallback = "") => {
  const normalizedValue = String(value || fallback)
    .replaceAll("_", " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalizedValue) {
    return "";
  }

  return normalizedValue
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const formatStatus = (value, fallback = "Pending") =>
  String(value || fallback).replaceAll("_", " ");

const formatRefundMethod = (value) =>
  formatTitleCase(value, "Refund");

const maskLastFour = (value) => {
  const stringValue = String(value || "").trim();
  if (!stringValue) {
    return null;
  }

  if (stringValue.length <= 4) {
    return stringValue;
  }

  return `*******${stringValue.slice(-4)}`;
};

const isFinalRefundStatus = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  return [...SUCCESS_STATUSES, ...FAILED_STATUSES].some((item) =>
    normalizedStatus.includes(item),
  );
};

const isClaimableRefundDetail = (detail) =>
  Boolean(detail?.refund_url) && !isFinalRefundStatus(detail?.refund_status);

const getDetailKey = (detail, index, prefix) =>
  detail?.id ||
  detail?.refund_id ||
  detail?.giftcard_code ||
  detail?.exchange_order_number ||
  detail?.exchange_order_name ||
  `${prefix}-${index}`;

const ReturnDetails = ({
  detailsType,
  returnType,
  returnItems,
  setReturnItems,
}) => {
  const location = useLocation();
  const currentPath = stripVersionPrefix(location.pathname);
  const isTrackingPage = currentPath === "/tracking";

  // Selectors
  const rmsConfig = useSelector(getRmsConfig);
  const merchantConfig = useSelector(getMerchantConfig);

  // States
  const [submitDetails, setSubmitDetails] = useState(false);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [detailsMethod, setDetailsMethod] = useState(null);
  const [submittingDetails, setSubmittingDetails] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [copiedDetailKey, setCopiedDetailKey] = useState(null);

  const renderIcon = (sourceValue = "") => {
    const iconList = [
      ["gift_card", <CardGiftcardIcon className="refund-icon gift-card" />],
      ["upi", <UPIIcon className="refund-icon upi" />],
      ["bank_transfer", <AccountBalanceIcon className="refund-icon bank" />],
      ["refund_to_source", <AccountBalanceIcon className="refund-icon source" />],
      ["coupon", <ConfirmationNumberIcon className="refund-icon coupon" />],
      ["cash_on_delivery", <CashIcon />],
      ["cashgram_razorpay", <AccountBalanceIcon className="refund-icon bank" />],
    ];

    if (!sourceValue) return;

    const lower = sourceValue?.toLowerCase();
    const match = iconList.find(([key]) => lower.includes(key));

    // return the matched icon or the default one
    return match ? (
      match[1]
    ) : (
      <AccountBalanceWalletIcon className="refund-icon wallet" />
    );
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();
  const hasExchangeDetails =
    Array.isArray(returnItems?.exchange_details) &&
    returnItems.exchange_details.length > 0;
  const exchangeDetails = Array.isArray(returnItems?.exchange_details)
    ? returnItems.exchange_details
    : [];
  const hasRefundDetails =
    Array.isArray(returnItems?.refund_details) &&
    returnItems.refund_details.length > 0;
  const refundDetails = Array.isArray(returnItems?.refund_details)
    ? returnItems.refund_details
    : [];
  const sortedRefundDetails = [...refundDetails].sort(
    (firstDetail, secondDetail) =>
      Number(isClaimableRefundDetail(secondDetail)) -
      Number(isClaimableRefundDetail(firstDetail)),
  );
  const applicableRefundModes = Array.isArray(
    returnItems?.applicable_refund_modes,
  )
    ? returnItems.applicable_refund_modes
    : returnItems?.applicable_refund_modes
      ? [returnItems.applicable_refund_modes]
      : [];
  const shouldShowRefundDetailsForm = !applicableRefundModes.includes(
    "payment_link",
  );

  const copyDetailValue = async (copyKey, value) => {
    const textValue = String(value || "").trim();
    if (!textValue) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textValue);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = textValue;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopiedDetailKey(copyKey);
      setTimeout(() => {
        setCopiedDetailKey((currentKey) =>
          currentKey === copyKey ? null : currentKey,
        );
      }, 2000);
    } catch (error) {
      toast.error("Unable to copy. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    }
  };

  const renderDetailGrid = (rows, detailKey) => {
    const visibleRows = rows.filter((row) => row && row.value);

    if (visibleRows.length === 0) {
      return null;
    }

    return (
      <div className="refund-detail-grid">
        {visibleRows.map((row) => {
          const copyKey = `${detailKey}-${row.label}`;
          const copied = copiedDetailKey === copyKey;

          return (
            <div
              key={row.label}
              className={row.wide ? "refund-detail-cell wide" : "refund-detail-cell"}
            >
              <p className="refund-detail-label">{row.label}</p>
              <div className="refund-detail-value-row">
                {row.icon ? row.icon : null}
                <p className="refund-detail-value">{row.value}</p>
                {row.copyValue ? (
                  <button
                    type="button"
                    className={`refund-copy-button ${copied ? "copied" : ""}`}
                    aria-label={`Copy ${row.label}`}
                    onClick={() => copyDetailValue(copyKey, row.copyValue)}
                  >
                    {copied ? <CheckCircleIcon /> : <ContentCopyOutlinedIcon />}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (detailsType === "Exchange" && !hasExchangeDetails) {
    return null;
  }

  if (detailsType === "Refund" && !hasRefundDetails) {
    return null;
  }

  const handleSubmitUPIDetails = async ({ upi }) => {
    setSubmittingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone =
      returnItems?.customer_phone_number ||
      returnItems?.user_phone_number ||
      null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc: null,
          account_number: null,
          name: null,
          phone: phone ? Number(phone) : null,
          vpa: upi,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "upi",
        "",
        0,
        0,
        "",
        upi,
      );
      setDetailsSubmitted(true);
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setReturnItems({
        ...returnItems,
        refund_json: {
          upi: {
            upi_code: upi,
          },
        },
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : DETAILS_SUBMISSION_FALLBACK,
      );
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setSubmittingDetails(false);
    }
  };

  const handleSubmitBankDetails = async ({
    accountnum,
    name,
    confirmAccountnum,
    ifsc,
  }) => {
    setSubmittingDetails(true);
    setVerificationError("");
    const shouldVerify = rmsConfig.bank_details_verification_enabled;
    const phone =
      returnItems?.customer_phone_number ||
      returnItems?.user_phone_number ||
      null;

    try {
      if (shouldVerify) {
        await verifyBankDetailsApi({
          ifsc,
          account_number: Number(accountnum),
          name,
          phone: phone ? Number(phone) : null,
          vpa: null,
        });
      }

      await submitDetailsApi(
        merchantConfig.merchant_id,
        returnItems.id,
        "bank_transfer",
        name,
        Number(accountnum),
        Number(confirmAccountnum),
        ifsc,
        "",
      );
      setDetailsSubmitted(true);
      setReturnItems({
        ...returnItems,
        refund_json: {
          bank_details: {
            bank_account_holder_name: name,
            bank_account_number: accountnum,
          },
        },
      });
      toast.success("Details have been successfully submitted", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setSubmitDetails(false);
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        shouldVerify ? BANK_VERIFICATION_FALLBACK : DETAILS_SUBMISSION_FALLBACK,
      );
      setVerificationError(errorMessage);
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setSubmittingDetails(false);
    }
  };

  return (
    <div
      className={`request-details-group ${
        detailsType === "Exchange"
          ? "exchange-details-group"
          : "refund-details-group"
      }`}
    >
      {/* Excahnge Box  */}
      {detailsType === "Exchange" ? (
        <>
          {returnItems &&
            returnItems !== null &&
            exchangeDetails.length > 0 && (
              <div>
                <p className="title"> Exchange Order </p>
                {exchangeDetails.map((detail, index) => {
                  const exchangeStatus = formatStatus(detail?.exchange_status);
                  const exchangeTimestamp = formatTimestamp(
                    detail?.exchange_initiated_at,
                  );
                  const exchangeOrderLabel =
                    detail?.exchange_order_name ||
                    detail?.exchange_order_number ||
                    "Exchange order";

                  return (
                    <StyledRefundDetails
                      key={getDetailKey(detail, index, "exchange-detail")}
                      className="return-detail-card"
                      roundedEdgeButtons={
                        merchantConfig.use_rounded_edge_buttons
                      }
                      primary_color={merchantConfig.primary_theme_color}
                      refundStatus={detail?.exchange_status}
                    >
                      <div className="refund-details-header">
                        <div className="refund-details">
                          <div className="refund-title">
                            {detail?.exchange_order_url ? (
                              <a
                                className="exchange-order-link"
                                target="_blank"
                                rel="noreferrer"
                                href={detail.exchange_order_url}
                              >
                                {exchangeOrderLabel}
                                <OpenInNewIcon />
                              </a>
                            ) : (
                              <p className="refund-amount">
                                {exchangeOrderLabel}
                              </p>
                            )}
                            <p className="refund-status">
                              {exchangeStatus}
                            </p>
                          </div>
                        </div>
                        {(exchangeTimestamp.date || exchangeTimestamp.time) && (
                          <div className="refund-time">
                            {exchangeTimestamp.date && (
                              <p>{exchangeTimestamp.date}</p>
                            )}
                            {exchangeTimestamp.time && (
                              <p>{exchangeTimestamp.time}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </StyledRefundDetails>
                  );
                })}
              </div>
            )}
        </>
      ) : (
        detailsType === "Refund" && (
          <div>
            <div>
              <p className="title">Refund Details</p>
              {sortedRefundDetails.map((detail, index) => {
                    const detailKey = getDetailKey(
                      detail,
                      index,
                      "refund-detail",
                    );
                    const refundMethodKey = String(
                      detail?.refund_method || "",
                    );
                    const refundStatus = formatStatus(detail?.refund_status);
                    const refundMethod = formatRefundMethod(
                      refundMethodKey,
                    );
                    const refundTimestamp = formatTimestamp(
                      detail?.refund_initiated_at,
                    );
                    const maskedAccountNumber = maskLastFour(
                      detail?.account_number,
                    );
                    const maskedVpa = maskLastFour(detail?.vpa);
                    const shouldShowClaimUrl = isClaimableRefundDetail(detail);
                    const isBankTransfer =
                      refundMethodKey === "bank_transfer" ||
                      refundMethodKey === "bank_transfer_razorpayx" ||
                      refundMethodKey === "bank_transfer_cashfree";
                    const isUpi = refundMethodKey === "upi";
                    const refundDetailRows = [
                      {
                        label: "Refund Method",
                        value: refundMethod,
                        icon: renderIcon(refundMethodKey),
                      },
                      detail.refund_type && {
                        label: "Refund Type",
                        value: formatTitleCase(detail.refund_type),
                      },
                      isBankTransfer &&
                        rmsConfig.use_direct_transfer_for_refund &&
                        detail.account_holder_name && {
                          label: "Account Holder",
                          value: detail.account_holder_name,
                        },
                      isBankTransfer &&
                        rmsConfig.use_direct_transfer_for_refund &&
                        maskedAccountNumber && {
                          label: "Account Number",
                          value: maskedAccountNumber,
                        },
                      (isUpi ||
                        (isBankTransfer &&
                          rmsConfig.use_direct_transfer_for_refund)) &&
                        maskedVpa && {
                          label: "UPI ID",
                          value: maskedVpa,
                        },
                      detail.utr && {
                        label: "UTR No",
                        value: detail.utr,
                      },
                      refundMethodKey === "coupon" &&
                        detail?.refund_id && {
                          label: "Coupon Code",
                          value: detail.refund_id,
                          copyValue: detail.refund_id,
                        },
                      refundMethodKey === "gift_card" &&
                        detail?.giftcard_code && {
                          label: "Gift Card Code",
                          value: detail.giftcard_code,
                          copyValue: detail.giftcard_code,
                        },
                    ];

                    return (
                      <div
                        key={detailKey}
                        className="return-detail-item"
                      >
                        <StyledRefundDetails
                          className="return-detail-card"
                          roundedEdgeButtons={
                            merchantConfig.use_rounded_edge_buttons
                          }
                          primary_color={merchantConfig.primary_theme_color}
                          refundStatus={detail?.refund_status}
                        >
                          <div className="refund-details-header">
                            <div className="refund-details">
                              <div className="refund-title">
                                <p className="refund-amount">
                                  ₹ {detail?.refund_amount ?? "-"}
                                </p>
                                <p className="refund-status">
                                  {refundStatus}
                                </p>
                              </div>
                            </div>
                            {(refundTimestamp.date || refundTimestamp.time) && (
                              <div className="refund-time">
                                {refundTimestamp.date && (
                                  <p>{refundTimestamp.date}</p>
                                )}
                                {refundTimestamp.time && (
                                  <p>{refundTimestamp.time}</p>
                                )}
                              </div>
                            )}
                          </div>
                          {renderDetailGrid(refundDetailRows, detailKey)}
                          <div>
                            {!shouldShowRefundDetailsForm ||
                            (isBankTransfer &&
                              rmsConfig.use_direct_transfer_for_refund) ? null : (
                              isTrackingPage &&
                              (isBankTransfer || isUpi) &&
                              !detailsSubmitted && (
                                <div className="payment-method-card refund-detail-mode-card">
                                  <p
                                    className="payment-method-title"
                                    id="refund-options"
                                  >
                                    Please select the mode of refund
                                  </p>
                                  <div
                                    aria-labelledby="refund-options"
                                    className="payment-method-options"
                                    role="radiogroup"
                                  >
                                    {rmsConfig.customer_bank_enabled && (
                                      <button
                                        aria-checked={detailsMethod === "Bank"}
                                        className={`payment-method-option ${
                                          detailsMethod === "Bank" ? "selected" : ""
                                        }`}
                                        id="bank"
                                        onClick={() => {
                                          setSubmitDetails(true);
                                          setDetailsMethod("Bank");
                                        }}
                                        role="radio"
                                        type="button"
                                        value="transfer"
                                      >
                                        <CheckmarkControl
                                          className="payment-method-check"
                                          primaryColor={
                                            merchantConfig.primary_theme_color
                                          }
                                          selected={detailsMethod === "Bank"}
                                          size={18}
                                        />
                                        <span>Bank Account</span>
                                      </button>
                                    )}
                                    {rmsConfig.customer_upi_enabled && (
                                      <button
                                        aria-checked={detailsMethod === "UPI"}
                                        className={`payment-method-option ${
                                          detailsMethod === "UPI" ? "selected" : ""
                                        }`}
                                        id="upi"
                                        onClick={() => {
                                          setSubmitDetails(true);
                                          setDetailsMethod("UPI");
                                        }}
                                        role="radio"
                                        type="button"
                                        value="upi"
                                      >
                                        <CheckmarkControl
                                          className="payment-method-check"
                                          primaryColor={
                                            merchantConfig.primary_theme_color
                                          }
                                          selected={detailsMethod === "UPI"}
                                          size={18}
                                        />
                                        <span>UPI</span>
                                      </button>
                                    )}
                                  </div>

                                  <BottomSheetDrawer
                                    open={submitDetails}
                                    onClose={() => setSubmitDetails(false)}
                                    rounded={
                                      merchantConfig.use_rounded_edge_buttons
                                    }
                                  >
                                    <StyledRefundDetailsDrawer
                                      rounded={
                                        merchantConfig.use_rounded_edge_buttons
                                      }
                                    >
                                      <FloatingCloseButton
                                        ariaLabel="Close refund details"
                                        className="refund-details-drawer-close"
                                        iconSize={18}
                                        onClick={() => setSubmitDetails(false)}
                                      />
                                      <div className="refund-details-drawer-header">
                                        <p>
                                          Enter {detailsMethod || "Refund"}{" "}
                                          Details
                                        </p>
                                      </div>
                                      {detailsMethod === "Bank" ? (
                                        <BankForm
                                          handleSubmitBankDetails={
                                            handleSubmitBankDetails
                                          }
                                          submittingDetails={submittingDetails}
                                          errorMessage={verificationError}
                                        />
                                      ) : (
                                        <form
                                          onSubmit={handleSubmit(
                                            handleSubmitUPIDetails,
                                          )}
                                          className="refund-details-upi-form"
                                        >
                                          <Controller
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                              required: true,
                                              validate: (value) =>
                                                !/@paytm$/i.test(value) ||
                                                "Paytm UPI IDs are not supported",
                                              pattern: {
                                                value:
                                                  /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
                                                message: "Invalid UPI ID",
                                              },
                                            }}
                                            name="upi"
                                            render={({ field }) => (
                                              <TextField
                                                variant="outlined"
                                                fullWidth
                                                id="upi"
                                                label="UPI ID"
                                                error={Boolean(errors.upi)}
                                                helperText={
                                                  errors.upi
                                                    ? errors.upi.type ===
                                                      "validate"
                                                      ? "Paytm UPI IDs are not supported"
                                                      : errors.upi.type ===
                                                          "pattern"
                                                        ? "Invalid UPI ID"
                                                        : "UPI code is required"
                                                    : ""
                                                }
                                                inputProps={{ type: "text" }}
                                                {...field}
                                              />
                                            )}
                                          />
                                          {verificationError && (
                                            <p className="refund-details-error">
                                              {verificationError}
                                            </p>
                                          )}
                                          <Button
                                            type="submit"
                                            color="primary"
                                            variant="contained"
                                            disabled={submittingDetails}
                                            className="refund-details-submit"
                                          >
                                            {submittingDetails
                                              ? "Submitting..."
                                              : "Submit"}
                                          </Button>
                                        </form>
                                      )}
                                    </StyledRefundDetailsDrawer>
                                  </BottomSheetDrawer>
                                </div>
                              )
                            )}
                            {refundMethodKey === "coupon" &&
                              !detail?.refund_id && (
                                <p className="refund-detail-pending-message">
                                  Coupon details will be shared soon.
                                </p>
                              )}
                            {refundMethodKey === "gift_card" &&
                              !detail?.giftcard_code && (
                                <p className="refund-detail-pending-message">
                                  Gift card details will be shared soon.
                                </p>
                              )}
                          </div>
                          {shouldShowClaimUrl && (
                            <div className="claim-refund-action">
                              <a
                                href={detail.refund_url}
                                target="_blank"
                                rel="noreferrer"
                                className="claim-refund-link"
                              >
                                <Button
                                  color="primary"
                                  variant="contained"
                                  fullWidth
                                >
                                  Claim your refund
                                </Button>
                              </a>
                            </div>
                          )}
                        </StyledRefundDetails>
                      </div>
                    );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ReturnDetails;
